import React, { useEffect, useState, useCallback, useRef } from 'react';
import { collection, query, orderBy, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useLocation } from '../contexts/LocationContext';
import { useNavigate } from 'react-router-dom';
import { useFavorites } from '../hooks/useLocalStorage';
import ShopCard from '../components/user/ShopCard';
import Loading from '../components/common/Loading';
import {
  Search, MapPin, CheckCircle, HelpCircle, RefreshCw, Layers,
  Scissors, ShoppingCart, Beef, Pill, Coffee, UtensilsCrossed,
  Shirt, Smartphone, BookOpen, Leaf, Store, Gem,
} from 'lucide-react';

/* ─────────────────────────────────────────────────────────────────
   FILTRES
───────────────────────────────────────────────────────────────── */
const FILTERS = [
  { key: 'all',       label: 'Tous',        icon: Layers      },
  { key: 'open',      label: 'Ouverts',     icon: CheckCircle },
  { key: 'nearby',    label: 'Pres de moi', icon: MapPin      },
  { key: 'uncertain', label: 'Incertains',  icon: HelpCircle  },
  { key: 'be_back',   label: 'De retour',   icon: RefreshCw   },
];

/* ─────────────────────────────────────────────────────────────────
   TYPES DE COMMERCE — icônes + labels de recherche
───────────────────────────────────────────────────────────────── */
const COMMERCE_ICONS = {
  boulangerie:    { icon: Store,           color: '#d97706' },
  epicerie:       { icon: ShoppingCart,    color: '#2d3f8f' },
  boucherie:      { icon: Beef,            color: '#dc2626' },
  pharmacie:      { icon: Pill,            color: '#059669' },
  cafe:           { icon: Coffee,          color: '#78350f' },
  restaurant:     { icon: UtensilsCrossed, color: '#7c3aed' },
  coiffeur:       { icon: Scissors,        color: '#db2777' },
  vetements:      { icon: Shirt,           color: '#0891b2' },
  electronique:   { icon: Smartphone,      color: '#1b2a6b' },
  librairie:      { icon: BookOpen,        color: '#b45309' },
  fruits_legumes: { icon: Leaf,            color: '#16a34a' },
  bijouterie:     { icon: Gem,             color: '#2976b6' },
  autre:          { icon: Store,           color: '#6b7280' },
};

/* Labels humains — utilisés pour la recherche par type */
const COMMERCE_LABELS = {
  boulangerie:    ['boulangerie', 'boulanger', 'pain', 'viennoiserie'],
  epicerie:       ['epicerie', 'épicerie', 'alimentation', 'superette', 'supérette'],
  boucherie:      ['boucherie', 'boucher', 'viande', 'agneau', 'poulet'],
  pharmacie:      ['pharmacie', 'pharmacien', 'medicament', 'médicament', 'sante', 'santé'],
  cafe:           ['cafe', 'café', 'snack', 'boisson', 'sandwicherie'],
  restaurant:     ['restaurant', 'resto', 'pizzeria', 'traiteur', 'repas'],
  coiffeur:       ['coiffeur', 'coiffure', 'barbier', 'salon', 'cheveux'],
  vetements:      ['vetements', 'vêtements', 'mode', 'habit', 'fringues', 'textile'],
  electronique:   ['electronique', 'électronique', 'telephone', 'téléphone', 'informatique', 'tech'],
  librairie:      ['librairie', 'livre', 'papeterie', 'papier'],
  fruits_legumes: ['fruits', 'legumes', 'légumes', 'primeur', 'maraicher', 'maraîcher'],
  bijouterie:     ['bijouterie', 'bijoux', 'or', 'argent', 'joaillerie'],
  autre:          ['autre', 'divers'],
};

/* ─────────────────────────────────────────────────────────────────
   RANG DU PACK
───────────────────────────────────────────────────────────────── */
const getPackRank = shop => {
  const p = shop.pack || 'free';
  if (p === 'free') return 99;
  const ranks = { pro: 0, premium: 1, starter: 2 };
  if (!ranks.hasOwnProperty(p)) return 99;
  const raw = shop.packExpiry;
  if (raw) {
    const d = raw?.toDate ? raw.toDate() : new Date(raw);
    if (!isNaN(d.getTime()) && d.getTime() <= Date.now()) return 99;
  }
  return ranks[p];
};

/* ─────────────────────────────────────────────────────────────────
   RANG DU STATUT (pour tri dans les résultats de recherche)
───────────────────────────────────────────────────────────────── */
const STATUS_RANK = { open: 0, be_back: 1, uncertain: 2, closed: 3 };
const getStatusRank = shop => STATUS_RANK[shop.status] ?? 3;

/* ─────────────────────────────────────────────────────────────────
   RECHERCHE — par nom ET par type de commerce
   Retourne les shops triés : pack → statut ouvert → distance
───────────────────────────────────────────────────────────────── */
const searchShops = (shops, term, location, calculateDistance) => {
  if (!term.trim()) return shops;
  const t = term.toLowerCase().trim();

  const matched = shops.filter(shop => {
    // 1. Nom du magasin
    if (shop.name?.toLowerCase().includes(t)) return true;
    // 2. Adresse
    if (shop.address?.toLowerCase().includes(t)) return true;
    // 3. Code type exact (ex: "boucherie")
    if (shop.commerceType?.toLowerCase().includes(t)) return true;
    // 4. Labels synonymes du type
    const synonyms = COMMERCE_LABELS[shop.commerceType] || [];
    if (synonyms.some(s => s.includes(t) || t.includes(s))) return true;
    return false;
  });

  // Enrichir avec distance
  const withDist = location
    ? matched.map(s => ({
        ...s,
        distance: calculateDistance(location.lat, location.lng, s.latitude, s.longitude),
      }))
    : matched.map(s => ({ ...s, distance: Infinity }));

  // Trier : pack → statut → distance
  return withDist.sort((a, b) => {
    const packDiff = getPackRank(a) - getPackRank(b);
    if (packDiff !== 0) return packDiff;
    const statDiff = getStatusRank(a) - getStatusRank(b);
    if (statDiff !== 0) return statDiff;
    return (a.distance ?? Infinity) - (b.distance ?? Infinity);
  });
};

/* ─────────────────────────────────────────────────────────────────
   SÉLECTION CARROUSEL — max 6
   Pro → Premium → Starter → Favoris → Plus proches
───────────────────────────────────────────────────────────────── */
const selectCarouselShops = (shops, favorites, location, calculateDistance) => {
  const eligible = shops.filter(s =>
    (!s.approval || s.approval === 'approved') && s.suspended !== true
  );
  const withDist = eligible.map(s => ({
    ...s,
    _dist: location
      ? calculateDistance(location.lat, location.lng, s.latitude, s.longitude)
      : Infinity,
  }));
  const paid = withDist
    .filter(s => getPackRank(s) < 99)
    .sort((a, b) => (getPackRank(a) - getPackRank(b)) || (a._dist - b._dist));
  const paidIds = new Set(paid.map(s => s.id));
  const favs = withDist
    .filter(s => favorites.includes(s.id) && !paidIds.has(s.id))
    .sort((a, b) => a._dist - b._dist);
  const favIds = new Set(favs.map(s => s.id));
  const nearby = withDist
    .filter(s => !paidIds.has(s.id) && !favIds.has(s.id))
    .sort((a, b) => a._dist - b._dist);
  return [...paid, ...favs, ...nearby].slice(0, 6);
};

const fmtDist = d =>
  !d || d === Infinity ? null : d < 1 ? `${Math.round(d * 1000)}m` : `${d.toFixed(1)}km`;

/* ─────────────────────────────────────────────────────────────────
   STATUT — gradient sémantique (fixe, signification universelle)
   Corps de carte — 100% variables CSS (thème-aware)
───────────────────────────────────────────────────────────────── */
const STATUS_GRADIENT = {
  open:      'linear-gradient(145deg, #00b396 0%, #00c9a7 60%, #00e5c0 100%)',
  closed:    'linear-gradient(145deg, #b91c1c 0%, #dc2626 60%, #ef4444 100%)',
  uncertain: 'linear-gradient(145deg, #b45309 0%, #d97706 60%, #f59e0b 100%)',
  be_back:   'linear-gradient(145deg, #1d4ed8 0%, #2563eb 60%, #3b82f6 100%)',
};
const STATUS_BADGE_LABEL = {
  open:      'Ouvert',
  closed:    'Fermé',
  uncertain: 'Incertain',
  be_back:   'Bientôt',
};
const PACK_BADGE = {
  pro:     { label: '★ Pro',     color: '#fbbf24' },
  premium: { label: '★ Premium', color: '#c4b5fd' },
  starter: { label: '★ Starter', color: '#93c5fd' },
};

/* ─────────────────────────────────────────────────────────────────
   HERO CARD
───────────────────────────────────────────────────────────────── */
const HeroCard = ({ shop, onClick }) => {
  const { icon: Icon } = COMMERCE_ICONS[shop.commerceType] || COMMERCE_ICONS.autre;
  const grad  = STATUS_GRADIENT[shop.status] || STATUS_GRADIENT.closed;
  const label = STATUS_BADGE_LABEL[shop.status] || 'Fermé';
  const pulse = shop.status === 'open' || shop.status === 'be_back' || shop.status === 'uncertain';
  const pack  = getPackRank(shop) < 99 ? PACK_BADGE[shop.pack] : null;
  const dist  = fmtDist(shop._dist);
  const isFav = shop._isFav;

  return (
    <div
      onClick={onClick}
      style={{
        flex: '0 0 260px',
        borderRadius: 'var(--r-lg)',
        overflow: 'hidden',
        cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
        userSelect: 'none',
        boxShadow: 'var(--neu-sm)',
      }}
    >
      {/* Zone statut — gradient sémantique */}
      <div style={{
        height: 140,
        background: grad,
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Cercles déco */}
        <div style={{ position:'absolute', right:-20, bottom:-20, width:110, height:110, borderRadius:'50%', border:'1px solid rgba(255,255,255,0.10)', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', right:8,   bottom:8,   width:60,  height:60,  borderRadius:'50%', border:'1px solid rgba(255,255,255,0.07)', pointerEvents:'none' }}/>

        {/* Icône type */}
        <div style={{
          position:'absolute', top:12, left:12,
          width:40, height:40, borderRadius:'var(--r-sm)',
          background:'rgba(255,255,255,0.18)',
          border:'1px solid rgba(255,255,255,0.25)',
          display:'flex', alignItems:'center', justifyContent:'center',
        }}>
          <Icon size={18} color="#fff" strokeWidth={2}/>
        </div>

        {/* Badge statut */}
        <div style={{
          position:'absolute', top:12, right:12,
          display:'inline-flex', alignItems:'center', gap:5,
          padding:'4px 9px', borderRadius:'var(--r-full)',
          background:'rgba(0,0,0,0.25)',
          backdropFilter:'blur(6px)',
          fontSize:10, fontWeight:700, color:'#fff',
          letterSpacing:'0.2px',
        }}>
          <span style={{
            width:5, height:5, borderRadius:'50%',
            background:'rgba(255,255,255,0.9)', flexShrink:0,
            animation: pulse ? 'pulseDot 2.2s ease infinite' : 'none',
          }}/>
          {label}
        </div>

        {/* Badge bas-gauche : pack > favori > distance */}
        {pack ? (
          <div style={{
            position:'absolute', bottom:10, left:12,
            padding:'3px 8px', borderRadius:'var(--r-full)',
            background:'rgba(0,0,0,0.30)', backdropFilter:'blur(6px)',
            fontSize:10, fontWeight:800, color: pack.color,
          }}>
            {pack.label}
          </div>
        ) : isFav ? (
          <div style={{
            position:'absolute', bottom:10, left:12,
            padding:'3px 8px', borderRadius:'var(--r-full)',
            background:'rgba(0,0,0,0.30)', backdropFilter:'blur(6px)',
            fontSize:10, fontWeight:800, color:'#fca5a5',
          }}>
            ♥ Favori
          </div>
        ) : dist ? (
          <div style={{
            position:'absolute', bottom:10, left:12,
            padding:'3px 8px', borderRadius:'var(--r-full)',
            background:'rgba(0,0,0,0.30)', backdropFilter:'blur(6px)',
            fontSize:10, fontWeight:700, color:'rgba(255,255,255,0.9)',
          }}>
            📍 {dist}
          </div>
        ) : null}
      </div>

      {/* Corps — 100% thème-aware */}
      <div style={{
        background: 'var(--surface-card)',
        padding: '10px 12px 12px',
        borderTop: '1px solid var(--outline)',
      }}>
        <div style={{
          fontSize:14, fontWeight:800,
          color:'var(--text)',
          letterSpacing:'-0.3px', lineHeight:1.2,
          whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
          marginBottom:3,
        }}>
          {shop.name}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:5 }}>
          <span style={{
            fontSize:10, fontWeight:700,
            color:'var(--text-3)',
            textTransform:'uppercase', letterSpacing:'0.5px',
          }}>
            {shop.commerceType?.replace('_',' ') || 'Commerce'}
          </span>
          {dist && (pack || isFav) && (
            <>
              <span style={{ width:3, height:3, borderRadius:'50%', background:'var(--outline)', flexShrink:0 }}/>
              <span style={{ fontSize:10, fontWeight:700, color:'var(--primary)' }}>{dist}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   HERO CAROUSEL — VERSION INFINIE LOOPING + AUTO-SCROLL
   Auto-scroll toutes les 4s, pause au survol/toucher
───────────────────────────────────────────────────────────────── */
const HeroCarousel = ({ shops, onShopClick }) => {
  const originalCount = shops.length;
  const loopedShops = originalCount > 0 ? [...shops, ...shops] : [];
  
  const [current, setCurrent] = useState(0);
  const [isJumping, setIsJumping] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const trackRef = useRef(null);
  const touchStartX = useRef(null);
  const mouseStartX = useRef(null);
  const didDrag = useRef(false);
  const autoScrollRef = useRef(null);

  // Gestion du looping infini avec saut invisible
  useEffect(() => {
    if (!trackRef.current || originalCount === 0) return;
    
    if (current >= originalCount) {
      setIsJumping(true);
      const timer = setTimeout(() => {
        setCurrent(prev => prev - originalCount);
        requestAnimationFrame(() => {
          setIsJumping(false);
        });
      }, 420);
      return () => clearTimeout(timer);
    }
    
    if (current < 0) {
      setIsJumping(true);
      const timer = setTimeout(() => {
        setCurrent(originalCount - 1);
        requestAnimationFrame(() => {
          setIsJumping(false);
        });
      }, 420);
      return () => clearTimeout(timer);
    }
  }, [current, originalCount]);

  // Appliquer la transformation
  useEffect(() => {
    if (!trackRef.current) return;
    const translateX = current * (260 + 12);
    trackRef.current.style.transform = `translateX(-${translateX}px)`;
    trackRef.current.style.transition = isJumping 
      ? 'none' 
      : 'transform 0.42s cubic-bezier(0.25,1,0.5,1)';
  }, [current, isJumping]);

  // AUTO-SCROLL : défilement automatique toutes les 4 secondes
  useEffect(() => {
    if (isPaused || originalCount <= 1) return;
    
    autoScrollRef.current = setInterval(() => {
      setCurrent(prev => prev + 1);
    }, 4000); // 4 secondes
    
    return () => {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }
    };
  }, [isPaused, originalCount]);

  const goTo = useCallback((idx) => {
    if (idx < 0) {
      setCurrent(originalCount - 1);
    } else if (idx >= originalCount) {
      setCurrent(idx);
    } else {
      setCurrent(idx);
    }
  }, [originalCount]);

  // Pause auto-scroll sur interaction
  const handleMouseEnter = () => setIsPaused(true);
  const handleMouseLeave = () => setIsPaused(false);
  
  const onTouchStart = (e) => { 
    setIsPaused(true);
    touchStartX.current = e.touches[0].clientX; 
  };
  
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const d = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(d) > 40) {
      d > 0 ? goTo(current + 1) : goTo(current - 1);
    }
    touchStartX.current = null;
    // Reprendre auto-scroll après 1 seconde d'inactivité
    setTimeout(() => setIsPaused(false), 1000);
  };

  const onMouseDown = (e) => { 
    setIsPaused(true);
    mouseStartX.current = e.clientX; 
    didDrag.current = false; 
  };
  
  const onMouseMove = (e) => { 
    if (mouseStartX.current && Math.abs(e.clientX - mouseStartX.current) > 6) {
      didDrag.current = true; 
    }
  };
  
  const onMouseUp = (e) => {
    if (!mouseStartX.current) return;
    const d = mouseStartX.current - e.clientX;
    if (Math.abs(d) > 40) {
      d > 0 ? goTo(current + 1) : goTo(current - 1);
    }
    mouseStartX.current = null;
    // Reprendre auto-scroll après 1 seconde
    setTimeout(() => setIsPaused(false), 1000);
  };

  if (!shops.length) return null;

  const activeDotIndex = ((current % originalCount) + originalCount) % originalCount;

  return (
    <div
      className="hero-carousel"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
    >
      <div className="hero-carousel__blob"/>
      <p className="hero-carousel__label">Commerces mis en avant</p>
      <h2 className="hero-carousel__title">À la une</h2>

      <div style={{ overflow:'hidden', margin:'0 -1rem' }}>
        <div
          ref={trackRef}
          style={{
            display:'flex', 
            gap:12,
            padding:'2px 1rem 4px',
            willChange:'transform',
            cursor:'grab',
          }}
        >
          {loopedShops.map((shop, idx) => (
            <HeroCard
              key={`${shop.id}-loop-${idx}`}
              shop={shop}
              onClick={() => { if (!didDrag.current) onShopClick(shop.id); }}
            />
          ))}
        </div>
      </div>

      <div className="hero-dots" role="tablist">
        {shops.map((_, i) => (
          <button
            key={i}
            role="tab"
            aria-selected={i === activeDotIndex}
            aria-label={`Slide ${i + 1}`}
            className={`hero-dot${i === activeDotIndex ? ' hero-dot--active' : ''}`}
            onClick={() => {
              goTo(i);
              setIsPaused(true);
              setTimeout(() => setIsPaused(false), 4000); // Pause 4s après clic manuel
            }}
          />
        ))}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   BARRE STICKY — SANS masquage au scroll
   + Gestion focus/blur pour la recherche
───────────────────────────────────────────────────────────────── */
const StickyBar = ({ searchTerm, onSearch, filter, onFilter, onSearchFocus, onSearchBlur }) => {
  return (
    <div className="search-sticky-bar">
      <div className="search-bar">
        <Search size={17} className="search-icon"/>
        <input
          type="search"
          placeholder="Rechercher un magasin, boucherie…"
          value={searchTerm}
          onChange={e => onSearch(e.target.value)}
          onFocus={onSearchFocus}
          onBlur={onSearchBlur}
          autoComplete="off"
          aria-label="Rechercher"
        />
      </div>
      {/* Filtres toujours visibles (plus de masquage au scroll) */}
      <div className="filter-bar">
        {FILTERS.map(f => {
          const Icon = f.icon;
          return (
            <button
              key={f.key}
              className={`filter-chip${filter === f.key ? ' active' : ''}`}
              onClick={() => onFilter(f.key)}
              aria-pressed={filter === f.key}
            >
              {Icon && <Icon size={13} strokeWidth={2.5}/>}
              {f.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   ÉTAT VIDE
───────────────────────────────────────────────────────────────── */
const EMPTY_CONFIG = {
  nearby:    { icon:'📍', title:'Aucun commerce à proximité',     body:"Aucun magasin dans un rayon de 5 km. Vérifiez que la géolocalisation est activée.",  action:'Voir tous les commerces' },
  open:      { icon:'🔒', title:'Tous les commerces sont fermés', body:"Aucun magasin ouvert en ce moment. Revenez plus tard ou consultez les horaires.",     action:'Voir tous les commerces' },
  uncertain: { icon:'❓', title:'Aucun statut incertain',          body:"Bonne nouvelle — tous les commerçants ont confirmé leur statut récemment !",           action: null },
  be_back:   { icon:'⏱️', title:'Aucun retour en cours',          body:"Aucun commerçant n'a signalé une absence temporaire pour l'instant.",                  action: null },
  search:    { icon:'🔍', title:'Aucun résultat',                  body:"Essayez avec un autre terme ou un type de commerce (ex: boucherie, pharmacie).",      action:'Effacer la recherche' },
  default:   { icon:'🏪', title:'Aucun commerce disponible',       body:"Il n'y a pas encore de magasin enregistré. Invitez un commerçant à rejoindre Yeldi !", action: null },
};

const EmptyState = ({ filter, searchTerm, onReset }) => {
  const key = searchTerm ? 'search' : (EMPTY_CONFIG[filter] ? filter : 'default');
  const cfg = EMPTY_CONFIG[key];
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', padding:'2.5rem 1.5rem 3rem', textAlign:'center' }}>
      <div style={{ width:72, height:72, borderRadius:'50%', background:'var(--bg)', boxShadow:'var(--neu)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.9rem', marginBottom:'1.1rem' }}>
        {cfg.icon}
      </div>
      <h3 style={{ fontWeight:800, fontSize:'1rem', color:'var(--text)', marginBottom:'0.5rem', lineHeight:1.3 }}>
        {cfg.title}
      </h3>
      <p style={{ fontSize:'0.82rem', color:'var(--text-2)', lineHeight:1.6, maxWidth:280, marginBottom: cfg.action ? '1.25rem' : 0 }}>
        {cfg.body}
      </p>
      {cfg.action && (
        <button
          onClick={onReset}
          style={{ display:'inline-flex', alignItems:'center', gap:'0.4rem', padding:'0.65rem 1.25rem', borderRadius:'var(--r-full)', border:'1.5px solid var(--outline)', background:'var(--bg)', boxShadow:'var(--neu-sm)', color:'var(--primary)', fontWeight:700, fontSize:'0.83rem', cursor:'pointer', fontFamily:'inherit' }}
        >
          <RefreshCw size={13} strokeWidth={2.5}/>
          {cfg.action}
        </button>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   PAGE HOME
───────────────────────────────────────────────────────────────── */
const Home = () => {
  const [shops,      setShops]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [filter,     setFilter]     = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [error,      setError]      = useState(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const { location, calculateDistance, getCurrentPosition } = useLocation();
  const { favorites } = useFavorites();
  const navigate = useNavigate();

  useEffect(() => { getCurrentPosition().catch(() => {}); }, [getCurrentPosition]);

  useEffect(() => {
    /*
     * OPTIMISATION : filtrer approval côté Firestore — évite de charger
     * tous les shops en attente/rejetés pour chaque client connecté.
     *
     * Le filtre suspended est intentionnellement laissé côté client :
     * - shops suspendus = cas très rare → impact lecture négligeable
     * - évite un index composite complexe (approval + suspended + updatedAt)
     *   qui nécessitait une création manuelle dans Firebase Console
     *
     * Index requis : simple index sur (approval ASC, updatedAt DESC)
     * → se crée automatiquement via firestore.indexes.json au prochain deploy.
     */
    const q = query(
      collection(db, 'shops'),
      where('approval', '==', 'approved'),
      orderBy('updatedAt', 'desc')
    );
    const unsub = onSnapshot(
      q,
      snap  => { setShops(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); setError(null); },
      err   => { console.error(err); setError('Impossible de charger les magasins.'); setLoading(false); }
    );
    return () => unsub();
  }, []);

  /* Filtre suspended côté client (cas rare, impact négligeable) */
  const visibleShops = shops.filter(s => s.suspended !== true);

  const carouselShops = selectCarouselShops(visibleShops, favorites, location, calculateDistance)
    .map(s => ({ ...s, _isFav: favorites.includes(s.id) }));

  /* ── Détermination de l'affichage du carrousel ──
     Le carrousel s'affiche seulement si :
     - Filtre sur "all" (Tous)
     - ET Pas de focus sur la recherche
     - ET Pas de terme de recherche actif
  ── */
  const showCarousel = filter === 'all' && !isSearchFocused && !searchTerm.trim();

  /* ── Liste filtrée ── */
  const filtered = (() => {
    let r = [...visibleShops];

    if (filter === 'open')      r = r.filter(s => s.status === 'open');
    if (filter === 'uncertain') r = r.filter(s => s.status === 'uncertain');
    if (filter === 'be_back')   r = r.filter(s => s.status === 'be_back');

    if (searchTerm.trim()) {
      return searchShops(r, searchTerm, location, calculateDistance);
    }

    if (location) {
      r = r
        .map(s => ({ ...s, distance: calculateDistance(location.lat, location.lng, s.latitude, s.longitude) }))
        .sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
      if (filter === 'nearby') r = r.filter(s => s.distance <= 5);
    }
    return r;
  })();

  return (
    <div className="page-enter">

      {/* 1. Barre sticky — filtres toujours visibles */}
      <StickyBar
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        filter={filter}
        onFilter={setFilter}
        onSearchFocus={() => setIsSearchFocused(true)}
        onSearchBlur={() => setIsSearchFocused(false)}
      />

      {/* 2. Carrousel — affiché uniquement en mode "normal" */}
      {!loading && showCarousel && carouselShops.length > 0 && (
        <HeroCarousel
          shops={carouselShops}
          onShopClick={id => navigate(`/shop/${id}`)}
        />
      )}

      {/* 3. Erreur */}
      {error && (
        <div className="alert alert-error" style={{ margin:'0.75rem 1rem 0' }}>
          <span>{error}</span>
        </div>
      )}

      {/* 4. Compteur + type détecté */}
      {!loading && !error && filtered.length > 0 && (
        <div style={{ padding:'0.6rem 1rem 0.1rem', display:'flex', alignItems:'center', gap:'0.4rem' }}>
          <span style={{ fontSize:'0.74rem', color:'var(--text-3)', fontWeight:600 }}>
            {filtered.length} magasin{filtered.length > 1 ? 's' : ''}
            {searchTerm ? ` — "${searchTerm}"` : ''}
          </span>
        </div>
      )}

      {/* 5. Bannière GPS — filtre "Près de moi" sans géolocalisation */}
      {filter === 'nearby' && !location && !loading && (
        <div style={{
          margin: '0.75rem 1rem 0',
          padding: '0.85rem 1rem',
          borderRadius: 'var(--r-lg)',
          background: 'rgba(245,158,11,0.08)',
          border: '1px solid rgba(245,158,11,0.30)',
          display: 'flex', alignItems: 'flex-start', gap: '0.65rem',
        }}>
          <MapPin size={18} style={{ color: '#f59e0b', flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.2rem' }}>
              Géolocalisation désactivée
            </div>
            <div style={{ fontSize: '0.76rem', color: 'var(--text-2)', lineHeight: 1.5 }}>
              Pour voir les commerces près de vous, activez la localisation dans les paramètres de votre navigateur puis rechargez la page.
            </div>
          </div>
        </div>
      )}

      {/* 6. Liste avec padding ajusté */}
      {loading ? (
        <Loading message="Chargement des magasins…"/>
      ) : filtered.length === 0 ? (
        <EmptyState
          filter={filter}
          searchTerm={searchTerm}
          onReset={() => { setFilter('all'); setSearchTerm(''); }}
        />
      ) : (
        <div className="shop-list" style={{ 
          paddingTop: showCarousel ? '0.45rem' : '1rem', // Plus d'espace en haut si carrousel caché
          paddingLeft: '0.75rem',   
          paddingRight: '0.75rem',  
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          {filtered.map(shop => <ShopCard key={shop.id} shop={shop}/>)}
        </div>
      )}

    </div>
  );
};

export default Home;